import { useEffect, useMemo, useState } from "react";

import { InitialIconikItem, MetadataFilters } from "@/components/chat/types";

import { useApi } from "../useApi";
import { loadPlayAssets } from "./useAssets";

/** A play that has no media (empty asset set or a failed fetch). */
export interface NoMediaEntry {
  gameId: string;
  playId: string;
  title?: string;
  /**
   * The play's description, carried from the agent's answer (sourced from the
   * DataLake play-by-play `results.description`). A no-media play has no Iconik
   * asset to read a description from, so it comes from the answer, not `/assets`.
   * Absent when the agent's Media Assets table omitted the Description column.
   */
  description?: string;
}

export interface GroupedPlayAssets {
  /** True until every play has settled. Does NOT mean "show nothing yet". */
  loading: boolean;
  /**
   * Plays that have at least one asset, in their original order. Grows as
   * results arrive rather than appearing all at once.
   */
  withMedia: InitialIconikItem[];
  /**
   * Plays with no media, in their original order. Empty until every play has
   * settled, because these collapse into a single card whose contents would
   * otherwise grow and reflow as results land.
   */
  noMedia: NoMediaEntry[];
  /**
   * How many plays are still in flight.
   *
   * Not derivable from the two lists above: a play that has resolved with no
   * media is deliberately absent from both until the group settles, so
   * ``count - withMedia.length`` would overstate what is actually still
   * loading.
   */
  pending: number;
}

/** What one play's ``/assets`` fetch concluded. */
interface PlayOutcome {
  hasMedia: boolean;
  /** Title from ``/assets``; absent when the fetch failed. */
  title?: string;
}

/**
 * Results for one particular set of plays.
 *
 * ``key`` tags the state with the play set + filters it belongs to. The items
 * array is rebuilt on every render of the message (the markdown is re-parsed),
 * so outcomes are addressed by index — and an index is only meaningful against
 * the play set it was recorded for. Comparing ``key`` before reading outcomes
 * is what stops a stale set's results being paired with new plays during the
 * render after the inputs change but before the effect re-runs.
 */
interface GroupState {
  key: string;
  outcomes: (PlayOutcome | undefined)[];
  done: boolean;
}

/**
 * Coordinate the per-play asset fetches for a chat answer's accordions so the
 * parent can sort and group them: plays with media float to the top, plays
 * with no media collapse into a single card.
 *
 * Each play is loaded via {@link loadPlayAssets}, which shares the same module
 * cache `useAssets` uses — so the with-media accordions (which still fetch via
 * `useAssets`) render from cache with no second request.
 *
 * Results are revealed **incrementally**. This previously awaited a single
 * `Promise.all` and flipped one `loading` flag at the end, which meant the
 * slowest play gated every accordion in the answer. Measured against dev, one
 * `/assets` call has a p50 of 2.2s, a p90 of 2.7s and a p99 of 22.8s; taking
 * the maximum over the five plays of a typical thread put roughly 41% of cold
 * opens past 2.7s and about 5% past 20s. Each play now lands on its own, so a
 * single slow or tail-latency play delays only itself.
 *
 * Order still follows the agent's Media Assets table, not completion order:
 * outcomes are recorded by index and the lists are derived by walking the
 * plays in order, so a play that resolves late slots back into its own
 * position instead of being appended.
 */
const useGroupedPlayAssets = (
  items: InitialIconikItem[],
  filters?: MetadataFilters
): GroupedPlayAssets => {
  const { apiGet } = useApi();
  const [state, setState] = useState<GroupState>({
    key: "",
    outcomes: [],
    done: false,
  });

  // Stable dependency: re-run only when the set of plays (or the filters)
  // actually changes, not on every render (array/object identity is not
  // stable). Folding the filters in ensures switching filters re-groups
  // against freshly-filtered fetches rather than serving the prior grouping.
  const filtersKey = useMemo(
    () => (filters ? JSON.stringify(filters) : ""),
    [filters]
  );
  const itemsKey = useMemo(
    () => items.map((i) => `${i.gameId}|${i.playId}`).join(","),
    [items]
  );
  const groupKey = `${itemsKey}::${filtersKey}`;

  useEffect(() => {
    // Scoped to this single effect invocation, deliberately not a ref: a
    // shared flag would be reset to false by the next run, letting a
    // superseded fetch write its result over the current play set.
    let cancelled = false;
    const key = groupKey;
    const plays = items;
    const total = plays.length;

    setState({ key, outcomes: new Array(total).fill(undefined), done: total === 0 });
    if (total === 0) {
      return () => {
        cancelled = true;
      };
    }

    let remaining = total;
    const record = (index: number, outcome: PlayOutcome) => {
      if (cancelled) return;
      remaining -= 1;
      const settled = remaining === 0;
      setState((prev) => {
        // A late result from a previous play set must not be written into the
        // current one; indices would not line up.
        if (prev.key !== key) return prev;
        const outcomes = prev.outcomes.slice();
        outcomes[index] = outcome;
        return { key, outcomes, done: settled };
      });
    };

    plays.forEach((item, index) => {
      loadPlayAssets(apiGet, item.gameId, item.playId, filters).then(
        (data) => record(index, { hasMedia: data.items.length > 0, title: data.title }),
        (error) => {
          // A failed fetch is treated as "no media" (matches the prior
          // per-accordion behaviour, which showed "No Media Found").
          console.error(error);
          record(index, { hasMedia: false });
        }
      );
    });

    return () => {
      cancelled = true;
    };
    // itemsKey + filtersKey (via groupKey) capture the relevant changes;
    // apiGet is stable. items/filters are read through the captured `plays`.
  }, [groupKey, apiGet]);

  // Only read outcomes recorded for the play set currently being rendered.
  // Between an input change and the effect re-running, `state` still describes
  // the previous set, and its indices do not address these plays.
  const current = state.key === groupKey ? state : null;

  const withMedia = useMemo(
    () =>
      current ? items.filter((_, index) => current.outcomes[index]?.hasMedia === true) : [],
    [current, items]
  );

  const noMedia = useMemo<NoMediaEntry[]>(() => {
    if (!current || !current.done) return [];
    return items.reduce<NoMediaEntry[]>((entries, item, index) => {
      const outcome = current.outcomes[index];
      if (outcome && !outcome.hasMedia) {
        entries.push({
          gameId: item.gameId,
          playId: item.playId,
          title: outcome.title,
          // Description comes from the agent's answer (play-by-play), carried
          // on the item — not from the /assets fetch (a no-media play has no
          // Iconik asset to source it from).
          description: item.description,
        });
      }
      return entries;
    }, []);
  }, [current, items]);

  const pending = current
    ? current.outcomes.reduce((n, outcome) => (outcome === undefined ? n + 1 : n), 0)
    : items.length;

  return { loading: !current || !current.done, withMedia, noMedia, pending };
};

export default useGroupedPlayAssets;
